import React, { useCallback, useMemo, useState } from 'react';
import { Box, FlexList, Option, SelectDropdown, Text } from '@common-fe/common-fe';

import { ALL_OPTION, ALL_OPTION_KEY } from '@/common/constants';
import { ListItemBase, OptionKey } from '@/common/types';

import { DependentStatusEnum } from '../../employee.types';
import { useGeneralInfoStore } from '../PersonalInformation/GeneralInfo/stores';

import ActivationDependentModal from './ActivationDependentModal';
import EditDependentModal from './EditDependentModal';
import { useDependentsList } from './hooks';

const DEPENDENT_STATUS_OPTIONS: Option[] = [
  ALL_OPTION,
  {
    key: DependentStatusEnum.ACTIVE,
    value: DependentStatusEnum.ACTIVE,
    title: 'Active',
  },
  {
    key: DependentStatusEnum.INACTIVE,
    value: DependentStatusEnum.INACTIVE,
    title: 'Inactive',
  },
];

const headers: ListItemBase[] = [
  {
    key: 'fullName',
    title: 'Full Name',
    flex: 1.5,
  },
  {
    key: 'birthOn',
    title: 'Date of birth',
    flex: '1',
  },
  {
    key: 'relationship',
    title: 'Relationship',
    flex: 1,
  },
  {
    key: 'source',
    title: 'Source',
    flex: 1,
  },
  {
    key: 'status',
    title: 'Status',
    flex: 1,
  },
];

const Dependents = () => {
  const [filterStatuses, setFilterStatuses] = useState<OptionKey[]>([DependentStatusEnum.ACTIVE]);
  const onChangeValues = useCallback((statuses: OptionKey[]) => {
    if (statuses[statuses.length - 1] === ALL_OPTION_KEY) {
      return setFilterStatuses([ALL_OPTION_KEY]);
    }

    if (statuses[0] === ALL_OPTION_KEY && statuses.length > 1) {
      return setFilterStatuses(statuses.filter((status) => status !== ALL_OPTION_KEY));
    }

    setFilterStatuses(statuses);
  }, [setFilterStatuses]);
  const selectedStatuses = useMemo(() => {
    if (filterStatuses.some((status) => status === ALL_OPTION_KEY)) {
      return filterStatuses
        ?.filter((status) => status !== ALL_OPTION_KEY) as DependentStatusEnum[];
    } else if (filterStatuses.length === DEPENDENT_STATUS_OPTIONS.length - 1) {
      return;
    }

    return filterStatuses as DependentStatusEnum[];
  }, [filterStatuses]);
  const { employeeId } = useGeneralInfoStore((state) => state.state);
  const [activationDependentId, setActivationDependentId] = useState('');
  const [editDependentId, setEditDependentId] = useState('');
  const { data, isLoading, formattedDependents } = useDependentsList({
    employeeId,
    setActivationDependentId,
    setEditDependentId,
    ...selectedStatuses?.length ? { selectedStatuses } : {},
  });
  const activationDependent = useMemo(() => formattedDependents
    ?.find((item) => item?.dependentId === activationDependentId),
    [formattedDependents, activationDependentId]);
  const editingDependent = useMemo(() => formattedDependents
    ?.find((item) => item?.dependentId === editDependentId),
    [formattedDependents, editDependentId]);
  const noData = useMemo(() => !data.length && !isLoading && !selectedStatuses?.length, [
    data, isLoading, selectedStatuses,
  ]);
  const noDependentsTitle = useMemo(() => {
    const statusTitle = selectedStatuses?.[0]?.toLowerCase() || '';

    if (statusTitle) return `any ${statusTitle}`;

    return 'any';
  }, [selectedStatuses]);

  return (
    <Box data-testid="Dependents-wrapper">
      <Box direction="column" background="module" pad="spacing24" round="moduleRound">
        <Box direction="row" align="center" justify="between" pad={{ bottom: 'spacing16' }}>
          <Box direction="row" align="center" justify="between">
            <Text
              size="large"
              weight="bold"
              color="textBody"
            >
              Dependents: {data.length || 0}
            </Text>
          </Box>
          <Box width={{ min: 'control' }}>
            <SelectDropdown
              id="status"
              testId="DependentList-statusFilter"
              ellipsisMode
              activeTitle
              allMode
              name="Activity status"
              disabled={noData}
              prefix="Status"
              options={DEPENDENT_STATUS_OPTIONS}
              values={filterStatuses}
              onChangeValues={onChangeValues}
              inputStyle={{ minWidth: '180px', maxWidth: '180px' }}
            />
          </Box>
        </Box>

        {data.length ? (
          <FlexList
            headers={headers}
            loading={isLoading}
            rows={data}
            pad={{ horizontal: '0px', bottom: '0px' }}
          />
        ) : (
          <Box align="center" justify="center" pad={{ top: 'spacing16' }}>
            <Text color="textSecondary" style={{ lineHeight: '21px' }}>
              The employee doesn't have {noDependentsTitle} dependents yet.
            </Text>
          </Box>
        )}
      </Box>

      {activationDependent && (
        <ActivationDependentModal
          dependent={activationDependent}
          setActivationDependentId={() => setActivationDependentId('')}
        />
      )}
      {editingDependent && (
        <EditDependentModal
          dependent={editingDependent}
          setEditDependentId={() => setEditDependentId('')}
        />
      )}
    </Box>
  );
};
export default Dependents;
